import React, { useState, useEffect, useCallback, Fragment } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Chip,
    Grid,
    Button,
    CircularProgress,
    Divider,
    IconButton,
    Collapse,
    useMediaQuery,
    useTheme,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import configServ from '../../services/config';
import { useSelector } from 'react-redux';
import DownloadFullCaseReportPdf from '../../Components/Download/DownloadFullCaseReportPdf';
import toast from 'react-hot-toast';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';


// Priority Color Helper
const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
        case 'high': return 'error';
        case 'medium': return 'warning';
        case 'low': return 'info';
        default: return 'default';
    }
};

// Date Formatter Helper
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

// Row Component for Expandable Desktop Table
const DesktopRow = ({ row, index }) => {
    const [open, setOpen] = useState(false);

    return (
        <Fragment>
            <TableRow
                hover
                onClick={() => setOpen(!open)}
                sx={{ '& > *': { borderBottom: 'unset' }, cursor: 'pointer' }}
            >
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>{index + 1}</TableCell>
                <TableCell>
                    <Typography variant="body2" fontWeight="bold" color="primary">
                        {row.case_title || 'N/A'}
                    </Typography>
                    <Typography variant="caption" display="block">
                        <b>Case No:</b> {row.case_no || 'N/A'}
                    </Typography>
                    <Typography variant="caption" display="block" color="textSecondary">
                        <b>FIR No:</b> {row.fir_no || 'N/A'}
                    </Typography>
                </TableCell>
                <TableCell>
                    {/* <Typography variant="body2"><b>Category ID:</b> {row.case_category_id || 'N/A'}</Typography> */}
                    <Typography variant="caption" color="textSecondary" display="block">
                        <b>Type:</b> {row.case_type_name || 'N/A'}
                    </Typography>
                </TableCell>
                <TableCell>
                    <Chip label={row.stage_name || 'N/A'} size="small" variant="outlined" color="primary" />
                </TableCell>
                <TableCell>{row.court_name || 'N/A'}</TableCell>
                {/* <TableCell>{row.judge_name || 'N/A'}</TableCell> */}
                <TableCell sx={{ whitespace: 'nowrap' }}>{formatDate(row.filing_date)}</TableCell>
                <TableCell>{row.opposite_party || 'N/A'}</TableCell>
                {/* <TableCell>
                    <Chip
                        label={row.priority || 'Medium'}
                        size="small"
                        color={getPriorityColor(row.priority)}
                    />
                </TableCell> */}
                {/* <TableCell>
                    <Chip
                        label={row.status || 'Active'}
                        size="small"
                        color={row.status === 'Active' ? 'success' : 'default'}
                    />
                </TableCell> */}
                {/* <TableCell sx={{ maxWidth: 150 }}>
                    <Typography variant="caption" noWrap display="block">
                        {row.description || row.case_summary || 'N/A'}
                    </Typography>
                </TableCell> */}
                <TableCell>
                    {row.client_names && row.client_names.length > 0 ? (
                        row.client_names.map((client, idx) => (
                            <Chip key={idx} label={client} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                        ))
                    ) : 'N/A'}
                </TableCell>
                <TableCell align="center">
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            setOpen(!open);
                        }}
                    >
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
            </TableRow>

            {/* Expanded Sub-Table Row for Hearings */}
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={13}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                            <Typography variant="h6" gutterBottom component="div" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                                Hearing Details ({row.hearings ? row.hearings.length : 0})
                            </Typography>
                            {row.hearings && row.hearings.length > 0 ? (
                                <TableContainer component={Paper} elevation={1} sx={{ overflowX: 'auto' }}>
                                    <Table size="small">
                                        <TableHead sx={{ backgroundColor: '#1565c0' }}>
                                            <TableRow>
                                                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>S.No</TableCell>
                                                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Hearing Date</TableCell>
                                                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Next Hearing Date</TableCell>
                                                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Court Room</TableCell>
                                                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Judge Name</TableCell>
                                                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Hearing Status</TableCell>
                                                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Summary</TableCell>
                                                {/* <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Order Details</TableCell> */}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {row.hearings.map((h, hIdx) => (
                                                <TableRow key={h.id || hIdx} hover>
                                                    <TableCell>{hIdx + 1}</TableCell>
                                                    <TableCell sx={{ whitespace: 'nowrap' }}>{formatDate(h.hearing_date)}</TableCell>
                                                    <TableCell sx={{ whitespace: 'nowrap' }}>{formatDate(h.next_hearing_date)}</TableCell>
                                                    <TableCell>{h.court_room || 'N/A'}</TableCell>
                                                    <TableCell>{h.judge_name || 'N/A'}</TableCell>
                                                    <TableCell>
                                                        <Chip label={h.hearing_status || 'Pending'} size="small" color="info" variant="outlined" />
                                                    </TableCell>
                                                    <TableCell sx={{ minWidth: 150 }}>{h.summary || 'N/A'}</TableCell>
                                                    <TableCell sx={{ minWidth: 150 }}>{h.order_details || 'N/A'}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                                    No hearings recorded for this case.
                                </Typography>
                            )}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </Fragment>
    );
};

const CaseReport = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { user_id, company_id, companyName } = useSelector((state) => state.GlobalVariables);


    const [cases, setCases] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchType, setSearchType] = useState('case_no');
    const [searchValue, setSearchValue] = useState('');
    const [dateValue, setDateValue] = useState('');

    const fetchCaseReports = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                company_id: company_id,
                search_type: searchType,
                search: ['fir_no', 'case_no', 'client_name'].includes(searchType) ? searchValue : '',
                date: ['filing_date', 'hearing'].includes(searchType) ? dateValue : '',
            };

            const response = await configServ.getCaseReports(params);
            if (response && response.status && Array.isArray(response.data)) {
                setCases(response.data);
            } else {
                setCases([]);
            }
        } catch (error) {
            console.error("Error fetching case reports:", error);
            setCases([]);
        } finally {
            setLoading(false);
        }
    }, [company_id, searchType, searchValue, dateValue]);

    useEffect(() => {
        fetchCaseReports();
    }, []);

    const handleSearchTypeChange = (e) => {
        setSearchType(e.target.value);
        setSearchValue('');
        setDateValue('');
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        fetchCaseReports();
    };

    const [pdfLoading, setPdfLoading] = useState(false);
    const [companyLogo, setCompanyLogo] = useState("");

    const assignCompanyLogo = async () => {
        try {
            const result = await configServ.company_logo({ company_id: company_id });
            if (result && result.image) {
                // Assuming the result.image is already a base64 string
                setCompanyLogo(`data:image/png;base64,${result.image}`);
            } else {
                setCompanyLogo(null); // Set as null if no logo
            }
        } catch (e) {
            console.log(e);
            setCompanyLogo(null); // Handle error by clearing logo
        }
    };

    useEffect(() => {
        assignCompanyLogo();
    }, []);

    const handleDownloadPDF = async () => {

        try {
            await DownloadFullCaseReportPdf(cases, companyName, companyLogo);
            toast.success("PDF downloaded successfully");
        } catch (error) {
            console.error("Error downloading PDF:", error);
            toast.error("Failed to download PDF");
        }
    }

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, width: '100%' }}>
            {/* Header & Filter Card */}
            <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Typography variant="h5" sx={{ mb: 2.5, fontWeight: 'bold', color: '#1976d2' }}>
                    Case Report Management
                </Typography>

                <form onSubmit={handleSearchSubmit}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={4} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="search-type-label">Search By</InputLabel>
                                <Select
                                    labelId="search-type-label"
                                    value={searchType}
                                    label="Search By"
                                    onChange={handleSearchTypeChange}
                                >
                                    <MenuItem value="case_no">Case No</MenuItem>
                                    <MenuItem value="fir_no">FIR No</MenuItem>
                                    <MenuItem value="client_name">Client Name</MenuItem>
                                    <MenuItem value="filing_date">Filing Date</MenuItem>
                                    <MenuItem value="hearing">Hearing Date</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={5} md={4}>
                            {['filing_date', 'hearing'].includes(searchType) ? (
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="date"
                                    label="Select Date"
                                    InputLabelProps={{ shrink: true }}
                                    value={dateValue}
                                    onChange={(e) => setDateValue(e.target.value)}
                                />
                            ) : (
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Search..."
                                    placeholder={`Search by ${searchType.replace('_', ' ')}`}
                                    value={searchValue}
                                    onChange={(e) => setSearchValue(e.target.value)}
                                />
                            )}
                        </Grid>

                        <Grid item xs={12} sm={3} md={2}>
                            <Button
                                fullWidth
                                variant="contained"
                                startIcon={<SearchIcon />}
                                type="submit"
                                sx={{ height: '40px', backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#115293' } }}
                            >
                                Search
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={3} md={2}>
                            <Button
                                fullWidth
                                variant="contained"
                                color="error"
                                startIcon={pdfLoading ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdfIcon />}
                                onClick={handleDownloadPDF}
                                disabled={pdfLoading}
                                sx={{ height: '40px' }}
                            >
                                {pdfLoading ? 'Downloading...' : 'PDF'}
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </Paper>

            {/* Content Area */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
                    <CircularProgress />
                </Box>
            ) : cases.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                    <Typography color="textSecondary">No Case Reports Found</Typography>
                </Paper>
            ) : (
                <>
                    {/* DESKTOP VIEW */}
                    {!isMobile ? (
                        <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 2, overflowX: 'auto' }}>
                            <Table>
                                <TableHead sx={{ backgroundColor: '#1976d2' }}>
                                    <TableRow>
                                        <TableCell align="center" sx={{ color: '#ffffff', fontWeight: 'bold' }}>S.No</TableCell>
                                        <TableCell sx={{ color: '#ffffff', fontWeight: 'bold' }}>Case Details</TableCell>
                                        <TableCell sx={{ color: '#ffffff', fontWeight: 'bold' }}>Type</TableCell>
                                        <TableCell sx={{ color: '#ffffff', fontWeight: 'bold' }}>Stage</TableCell>
                                        <TableCell sx={{ color: '#ffffff', fontWeight: 'bold' }}>Court Name</TableCell>
                                        {/* <TableCell sx={{ color: '#ffffff', fontWeight: 'bold' }}>Judge Name</TableCell> */}
                                        <TableCell sx={{ color: '#ffffff', fontWeight: 'bold' }}>Filing Date</TableCell>
                                        <TableCell sx={{ color: '#ffffff', fontWeight: 'bold' }}>Opposite Party</TableCell>
                                        {/* <TableCell sx={{ color: '#ffffff', fontWeight: 'bold' }}>Priority</TableCell> */}
                                        {/* <TableCell sx={{ color: '#ffffff', fontWeight: 'bold' }}>Status</TableCell> */}
                                        {/* <TableCell sx={{ color: '#ffffff', fontWeight: 'bold' }}>Description</TableCell> */}
                                        <TableCell sx={{ color: '#ffffff', fontWeight: 'bold' }}>Clients</TableCell>
                                        <TableCell align="center" sx={{ color: '#ffffff', fontWeight: 'bold' }}>Hearings</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {cases.map((row, index) => (
                                        <DesktopRow key={row.id || index} row={row} index={index} />
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        /* MOBILE CARD VIEW */
                        <Box>
                            {cases.map((item, index) => (
                                <Card key={item.id || index} sx={{ mb: 2, borderRadius: 2 }} elevation={3}>
                                    <CardContent>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                            <Typography variant="subtitle1" color="primary" fontWeight="bold">
                                                #{index + 1} - {item.case_title || 'Untitled Case'}
                                            </Typography>
                                            <Chip label={item.status || 'Active'} color={item.status === 'Active' ? 'success' : 'default'} size="small" />
                                        </Box>

                                        <Divider sx={{ my: 1 }} />

                                        <Grid container spacing={1} sx={{ fontSize: '0.875rem' }}>
                                            <Grid item xs={6}><b>Case No:</b> {item.case_no || 'N/A'}</Grid>
                                            <Grid item xs={6}><b>FIR No:</b> {item.fir_no || 'N/A'}</Grid>
                                            {/* <Grid item xs={6}><b>Category ID:</b> {item.case_category_id || 'N/A'}</Grid> */}
                                            <Grid item xs={6}><b>Case Type:</b> {item.case_type_name || 'N/A'}</Grid>
                                            <Grid item xs={6}><b>Stage:</b> {item.stage_name || 'N/A'}</Grid>
                                            <Grid item xs={6}><b>Court:</b> {item.court_name || 'N/A'}</Grid>
                                            {/* <Grid item xs={6}><b>Judge:</b> {item.judge_name || 'N/A'}</Grid> */}
                                            <Grid item xs={6}><b>Filing Date:</b> {formatDate(item.filing_date)}</Grid>
                                            <Grid item xs={6}><b>Opp. Party:</b> {item.opposite_party || 'N/A'}</Grid>
                                            {/* <Grid item xs={6}><b>Priority:</b> {item.priority || 'Medium'}</Grid> */}
                                        </Grid>

                                        {/* <Typography variant="body2" sx={{ mt: 1 }}>
                                            <b>Description:</b> {item.description || item.case_summary || 'N/A'}
                                        </Typography> */}

                                        <Box sx={{ mt: 1 }}>
                                            <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>Clients:</Typography>
                                            {item.client_names && item.client_names.length > 0 ? (
                                                item.client_names.map((client, idx) => (
                                                    <Chip key={idx} label={client} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                                                ))
                                            ) : 'N/A'}
                                        </Box>

                                        {/* Mobile Hearings Sub-Accordion */}
                                        <Box sx={{ mt: 2 }}>
                                            {item.hearings && item.hearings.length > 0 ? (
                                                <Accordion elevation={0} variant="outlined">
                                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                        <Typography variant="body2" fontWeight="bold" color="primary">
                                                            Hearings ({item.hearings.length})
                                                        </Typography>
                                                    </AccordionSummary>
                                                    <AccordionDetails sx={{ p: 1, overflowX: 'auto' }}>
                                                        {item.hearings.map((h, hIdx) => (
                                                            <Box key={h.id || hIdx} sx={{ p: 1.5, mb: 1, bgcolor: '#f8fafc', borderRadius: 1, border: '1px solid #e2e8f0' }}>
                                                                <Typography variant="caption" display="block"><b>S.No:</b> {hIdx + 1}</Typography>
                                                                <Typography variant="caption" display="block"><b>Hearing Date:</b> {formatDate(h.hearing_date)}</Typography>
                                                                <Typography variant="caption" display="block"><b>Next Date:</b> {formatDate(h.next_hearing_date)}</Typography>
                                                                <Typography variant="caption" display="block"><b>Court Room:</b> {h.court_room || 'N/A'}</Typography>
                                                                <Typography variant="caption" display="block"><b>Judge:</b> {h.judge_name || 'N/A'}</Typography>
                                                                <Typography variant="caption" display="block"><b>Status:</b> {h.hearing_status || 'N/A'}</Typography>
                                                                <Typography variant="caption" display="block"><b>Summary:</b> {h.summary || 'N/A'}</Typography>
                                                                {/* <Typography variant="caption" display="block"><b>Order Details:</b> {h.order_details || 'N/A'}</Typography> */}
                                                            </Box>
                                                        ))}
                                                    </AccordionDetails>
                                                </Accordion>
                                            ) : (
                                                <Typography variant="caption" color="textSecondary">No Hearings</Typography>
                                            )}
                                        </Box>
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default CaseReport;